import React, { useEffect, useMemo, useState } from 'react';
import { useEditor, DEFAULT_TEXT_PROPS } from '../../context/EditorContext';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { LargeButton, WithTip } from './RibbonPrimitives';
import { GalleryTile, GalleryLabel, GalleryGrid } from './EffectGalleries';
import { buildTextStyles } from '../editor/ObjectRenderer';
import { WordArtTransformSample } from '../editor/WordArtText';
import { wordArtStyles, matchWordArtStyle, CLEAR_WORDART_PROPS, WORDART_TRANSFORMS } from '../../lib/wordart';
import { ChevronUp, ChevronDown, Ban } from 'lucide-react';

const hover = 'hover:bg-[hsl(var(--wp-command-hover))] active:bg-[hsl(var(--wp-command-pressed))]';

/** Publisher's tilted "A" WordArt glyph. */
export const WordArtIcon = ({ className = '', strokeWidth = 1.75 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M4 20 L11 4 L18 20" />
    <path d="M7.2 14.5 H14.8" />
    <path d="M17 6.5 c2.5 -1 4 0.5 3 3" />
  </svg>
);

/** "A" preview that shows a WordArt style exactly as the text renderer would (fill, outline, shadow, glow, reflection…). */
export const WordArtSample = ({ style, size = 26, text = 'A', font = 'Outfit' }) => {
  const t = buildTextStyles({ ...DEFAULT_TEXT_PROPS, ...style.props, fontFamily: font, fontWeight: 'bold', fontSize: size }, 1).text;
  const { color, background, WebkitTextStroke, textShadow, filter, WebkitBoxReflect } = t;
  const WebkitBackgroundClip = background ? 'text' : undefined, backgroundClip = WebkitBackgroundClip;
  return (
    <span aria-hidden="true" className="leading-none select-none inline-block" style={{ fontFamily: `'${font}', sans-serif`, fontWeight: 700, fontSize: size, paddingInline: 3, color, background, WebkitBackgroundClip, backgroundClip, WebkitTextStroke, textShadow, filter, WebkitBoxReflect }}>
      {text}
    </span>
  );
};

const RowButton = ({ icon: Icon, label, onClick, active, testId }) => (
  <button type="button" onClick={onClick} onMouseDown={(e) => e.preventDefault()} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-[13px] ${hover} ${active ? 'bg-primary/10' : ''}`} data-testid={testId}>
    {Icon && <Icon className="h-4 w-4 text-muted-foreground" />} {label}
  </button>
);

/** Transform gallery (No Transform, Follow Path, Warp) shared by the Text Box tab popover and the Insert menu. */
export const TransformGallery = ({ current = 'none', onPick, testPrefix = 'wordart-transform', color = '#1f3763' }) => {
  const groups = ['Follow Path', 'Warp'];
  return (
    <div data-testid={`${testPrefix}-gallery`}>
      <GalleryTile title="No Transform" active={current === 'none'} onClick={() => onPick('none')} testId={`${testPrefix}-none`} size={44}>
        <span className="font-outfit font-bold text-[15px] leading-none text-foreground" aria-hidden="true">abc</span>
      </GalleryTile>
      {groups.map((g) => (
        <React.Fragment key={g}>
          <GalleryLabel>{g}</GalleryLabel>
          <GalleryGrid cols={5}>
            {WORDART_TRANSFORMS.filter((t) => t.group === g).map((t) => (
              <GalleryTile key={t.id} title={t.name} active={current === t.id} onClick={() => onPick(t.id)} testId={`${testPrefix}-${t.id}`} size={44}>
                <WordArtTransformSample transform={t.id} size={38} color={color} />
              </GalleryTile>
            ))}
          </GalleryGrid>
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * In-ribbon WordArt Styles gallery (3 visible tiles + ▲ ▼ pager + "more" popover with the full 5×4 grid,
 * Clear WordArt and the Transform gallery). props: { p: text properties, set(patch), disabled }
 */
export const WordArtStylesGallery = ({ p = {}, set, disabled }) => {
  const { state } = useEditor();
  const styles = useMemo(() => wordArtStyles(state.schemeId), [state.schemeId]);
  const activeId = matchWordArtStyle(p, state.schemeId);
  const perRow = 3;
  const pages = Math.ceil(styles.length / perRow);
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(false);
  const currentTransform = p.wordArt?.transform || 'none';

  // Keep the applied style visible in the ribbon strip.
  useEffect(() => {
    if (!activeId) return;
    const idx = styles.findIndex((s) => s.id === activeId);
    if (idx >= 0) setPage(Math.floor(idx / perRow));
  }, [activeId, styles]);

  const apply = (st) => { set({ ...st.props, wordArt: { ...(p.wordArt || {}), styleId: st.id } }); setOpen(false); };
  const clear = () => { set({ ...CLEAR_WORDART_PROPS, wordArt: { ...(p.wordArt || {}), styleId: null } }); setOpen(false); };
  const setTransform = (id) => { set({ wordArt: { ...(p.wordArt || {}), transform: id } }); setOpen(false); };
  const sampleColor = p.textFill?.type === 'solid' ? p.textFill.color : p.color || '#1f3763';

  const pagerBtn = 'h-[18px] w-full flex items-center justify-center text-muted-foreground disabled:opacity-30 disabled:cursor-not-allowed ' + hover;

  return (
    <div className={`flex items-stretch rounded-sm border border-border bg-background h-[62px] ${disabled ? 'opacity-40 pointer-events-none' : ''}`} data-testid="wordart-styles-gallery" aria-disabled={disabled || undefined}>
      <div className="flex items-center gap-[3px] px-[3px]">
        {styles.slice(page * perRow, page * perRow + perRow).map((st) => (
          <GalleryTile key={st.id} size={46} title={st.name} active={st.id === activeId} onClick={() => apply(st)} testId={`wordart-style-${st.id}`}>
            <WordArtSample style={st} size={30} />
          </GalleryTile>
        ))}
      </div>
      <div className="flex flex-col w-[16px] border-l border-border">
        <WithTip tip="Previous row">
          <button type="button" className={pagerBtn} onClick={() => setPage((x) => Math.max(0, x - 1))} onMouseDown={(e) => e.preventDefault()} disabled={page === 0} aria-label="Previous WordArt styles" data-testid="wordart-gallery-up"><ChevronUp className="h-3 w-3" /></button>
        </WithTip>
        <WithTip tip="Next row">
          <button type="button" className={pagerBtn} onClick={() => setPage((x) => Math.min(pages - 1, x + 1))} onMouseDown={(e) => e.preventDefault()} disabled={page >= pages - 1} aria-label="Next WordArt styles" data-testid="wordart-gallery-down"><ChevronDown className="h-3 w-3" /></button>
        </WithTip>
        <Popover modal={false} open={open} onOpenChange={setOpen}>
          <WithTip tip="WordArt Styles: all styles, Clear WordArt and Transform">
            <PopoverTrigger asChild>
              <button type="button" className={`${pagerBtn} flex-1 flex-col gap-0 border-t border-border`} onMouseDown={(e) => e.preventDefault()} aria-label="More WordArt styles" data-testid="wordart-gallery-more" data-state={open ? 'open' : 'closed'}>
                <span className="block w-2 h-px bg-current" /><ChevronDown className="h-3 w-3 -mt-px" />
              </button>
            </PopoverTrigger>
          </WithTip>
          <PopoverContent align="start" className="w-[268px] p-2 bg-popover text-popover-foreground border border-border shadow-md z-[80] max-h-[70vh] overflow-y-auto" data-ribbon onOpenAutoFocus={(e) => e.preventDefault()} data-testid="wordart-styles-popover">
            <GalleryLabel>WordArt Styles</GalleryLabel>
            <GalleryGrid cols={5}>
              {styles.map((st) => (
                <GalleryTile key={st.id} title={st.name} active={st.id === activeId} onClick={() => apply(st)} testId={`wordart-style-all-${st.id}`}>
                  <WordArtSample style={st} size={26} />
                </GalleryTile>
              ))}
            </GalleryGrid>
            <DropdownMenuSeparator className="my-1.5" />
            <RowButton icon={Ban} label="Clear WordArt" onClick={clear} testId="wordart-clear" />
            <DropdownMenuSeparator className="my-1.5" />
            <GalleryLabel>Transform</GalleryLabel>
            <TransformGallery current={currentTransform} onPick={setTransform} color={sampleColor} />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

/** Insert ▸ WordArt: Plain WordArt Styles + WordArt Transform Styles. onInsert(style, transformId) */
export const WordArtInsertMenu = ({ onInsert, testId = 'ribbon-command-wordart' }) => {
  const { state } = useEditor();
  const styles = useMemo(() => wordArtStyles(state.schemeId), [state.schemeId]);
  const [open, setOpen] = useState(false);
  const pick = (st, transform) => { setOpen(false); onInsert(st, transform); };
  const transforms = WORDART_TRANSFORMS.filter((t) => t.id !== 'none');
  return (
    <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <LargeButton icon={WordArtIcon} label="WordArt" chevron tip="Insert decorative text with a WordArt style or transform" testId={testId} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[268px] p-2 bg-popover text-popover-foreground border border-border shadow-md z-[80] max-h-[70vh] overflow-y-auto" data-ribbon onCloseAutoFocus={(e) => e.preventDefault()} data-testid="wordart-insert-menu">
        <GalleryLabel>Plain WordArt Styles</GalleryLabel>
        <GalleryGrid cols={5}>
          {styles.map((st) => (
            <GalleryTile key={st.id} title={st.name} onClick={() => pick(st, 'none')} testId={`wordart-insert-${st.id}`}>
              <WordArtSample style={st} size={26} />
            </GalleryTile>
          ))}
        </GalleryGrid>
        <GalleryLabel>WordArt Transform Styles</GalleryLabel>
        <GalleryGrid cols={5}>
          {transforms.map((t, i) => {
            const st = styles[(i * 3) % styles.length];
            const color = st.props.textFill?.type === 'solid' && st.props.textFill.color !== '#ffffff' ? st.props.textFill.color : st.props.color === '#ffffff' ? '#1f3763' : st.props.color;
            return (
              <GalleryTile key={t.id} title={`${t.name}`} onClick={() => pick(st, t.id)} testId={`wordart-insert-transform-${t.id}`}>
                <WordArtTransformSample transform={t.id} size={38} color={color} />
              </GalleryTile>
            );
          })}
        </GalleryGrid>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
